/**
 * Hook aislado para el visor PDF que:
 * 1) Registra una apertura del documento al abrirlo.
 * 2) Consulta si hay progreso previo y, si lo hay, muestra un toast "Continuar leyendo".
 * 3) Al cerrar el visor, guarda silenciosamente la última página leída.
 *
 * No modifica el motor de renderizado del visor — solo se enchufa por props.
 *
 * Uso:
 *   const { jumpToPage } = useReadingProgress({
 *     isOpen, documentId, currentPage, onJumpToPage
 *   });
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { misDocumentosAPI } from '../services/api';

const useReadingProgress = ({ isOpen, documentId, currentPage, onJumpToPage }) => {
  // Refs para que no se re-disparen los efectos por cambios de página/cierre
  const currentPageRef = useRef(currentPage);
  const sessionKeyRef = useRef(null);

  // Mantener currentPage actualizado sin re-ejecutar effects
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // 1) Al abrir el visor: registrar apertura + consultar progreso previo
  useEffect(() => {
    if (!isOpen || !documentId) return;

    // Identificador único de esta sesión de lectura
    const sessionKey = `${documentId}::${Date.now()}`;
    sessionKeyRef.current = sessionKey;

    let cancelled = false;

    const init = async () => {
      try {
        // Disparar apertura (fire-and-forget). No bloquea el visor.
        misDocumentosAPI.registrarApertura(documentId).catch(() => {});

        // Consultar progreso previo
        const { data } = await misDocumentosAPI.getProgreso(documentId);
        if (cancelled || sessionKeyRef.current !== sessionKey) return;
        if (!data?.tiene_progreso) return;

        const ultimaPagina = Number(data.ultima_pagina) || 1;
        if (ultimaPagina <= 1) return;

        // Toast no invasivo con acción "Continuar"
        toast(`Quedaste en la página ${ultimaPagina}`, {
          description: 'Hacé clic para retomar la lectura donde la dejaste.',
          duration: 8000,
          action: {
            label: 'Continuar',
            onClick: () => {
              // 1) Actualiza el estado del visor (página actual)
              if (typeof onJumpToPage === 'function') {
                onJumpToPage(ultimaPagina);
              }
              // 2) En modo continuo, scrollear directo al elemento de la página.
              //    En modo single-page no existe [data-page-num], es no-op.
              setTimeout(() => {
                const el = document.querySelector(
                  `[data-page-num="${ultimaPagina}"]`
                );
                if (el) {
                  el.scrollIntoView({ behavior: 'auto', block: 'start' });
                }
              }, 150);
            },
          },
        });
      } catch (_err) {
        // Silencioso: si falla el backend, simplemente no mostramos el toast.
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [isOpen, documentId]);

  // 2) Al cerrar el visor (o cambiar de documento): guardar la última página
  useEffect(() => {
    return () => {
      const lastPage = currentPageRef.current;
      const docId = documentId;
      if (!docId || !lastPage || lastPage < 1) return;
      // Fire-and-forget: no bloquear el cierre del modal.
      misDocumentosAPI.guardarProgreso(docId, lastPage).catch(() => {});
    };
    // Importante: usamos cleanup que corre al cerrar O al cambiar de documento.
  }, [documentId]);

  // 3) Al cerrar (isOpen pasa a false), también guardar — cubre el caso donde
  //    el componente queda montado pero el modal se oculta.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen && documentId) {
      const lastPage = currentPageRef.current;
      if (lastPage >= 1) {
        misDocumentosAPI.guardarProgreso(documentId, lastPage).catch(() => {});
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, documentId]);
};

export default useReadingProgress;
